import { useRef, type DragEvent } from "react";
import type { Accent, DashboardSection } from "../lib/api";
import { PriceTable } from "./PriceTable";

interface Props {
  section: DashboardSection;
  /** When true, the card header carries a drag handle. */
  draggable?: boolean;
  isDragging?: boolean;
  isDragTarget?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onDragEnter?: () => void;
  onDragOver?: (e: DragEvent) => void;
  onDrop?: () => void;
}

const ACCENT_HEADER: Record<Accent, string> = {
  amber: "bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200",
  emerald: "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/15 dark:text-emerald-200",
  sky: "bg-sky-100 text-sky-900 dark:bg-sky-500/15 dark:text-sky-200",
  violet: "bg-violet-100 text-violet-900 dark:bg-violet-500/15 dark:text-violet-200",
  rose: "bg-rose-100 text-rose-900 dark:bg-rose-500/15 dark:text-rose-200",
  slate: "bg-slate-100 text-slate-900 dark:bg-slate-500/15 dark:text-slate-100",
  lime: "bg-lime-100 text-lime-900 dark:bg-lime-500/15 dark:text-lime-200",
  orange: "bg-orange-100 text-orange-900 dark:bg-orange-500/15 dark:text-orange-200",
};

export function ProductSection({
  section,
  draggable = false,
  isDragging = false,
  isDragTarget = false,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDragOver,
  onDrop,
}: Props) {
  // Only the handle is `draggable=true`; the rest of the card can still be
  // hovered/clicked normally and text in the table stays selectable. The
  // handle copies the whole card into the drag image so the visual feedback
  // matches the affected element.
  const sectionRef = useRef<HTMLElement>(null);

  const handleDragStart = (e: DragEvent<HTMLButtonElement>) => {
    if (sectionRef.current) {
      const rect = sectionRef.current.getBoundingClientRect();
      e.dataTransfer.setDragImage(sectionRef.current, e.clientX - rect.left, e.clientY - rect.top);
    }
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", section.id);
    onDragStart?.();
  };

  return (
    <section
      ref={sectionRef}
      className={
        "rounded-xl border bg-white dark:bg-slate-900 shadow-sm overflow-hidden transition " +
        (isDragging
          ? "opacity-50 border-slate-300 dark:border-slate-600"
          : isDragTarget
            ? "border-sky-400 dark:border-sky-500 ring-2 ring-sky-300/50 dark:ring-sky-500/40"
            : "border-slate-200 dark:border-slate-800")
      }
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <header
        className={`px-4 py-2.5 flex items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 ${ACCENT_HEADER[section.accent]}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {draggable && (
            <button
              type="button"
              draggable
              onDragStart={handleDragStart}
              onDragEnd={onDragEnd}
              className="shrink-0 cursor-grab active:cursor-grabbing opacity-50 hover:opacity-100 transition"
              title="Karte verschieben"
              aria-label="Karte verschieben"
            >
              <DragHandleIcon />
            </button>
          )}
          <h2 className="font-semibold text-sm sm:text-base truncate">{section.title}</h2>
        </div>
        <span className="text-xs font-mono opacity-80 shrink-0">{section.unit}</span>
      </header>
      <div className="px-3 sm:px-4 py-1">
        <PriceTable rows={section.rows} />
      </div>
    </section>
  );
}

function DragHandleIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <circle cx="9" cy="6" r="1.6" />
      <circle cx="15" cy="6" r="1.6" />
      <circle cx="9" cy="12" r="1.6" />
      <circle cx="15" cy="12" r="1.6" />
      <circle cx="9" cy="18" r="1.6" />
      <circle cx="15" cy="18" r="1.6" />
    </svg>
  );
}
